import { Button } from '@heroui/react'
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  MessageSquare,
} from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router'
import { Logo } from '../components/Logo'
import { useAuth } from '../context/AuthContext'
import { requestPasswordResetOtp, resetPasswordWithOtp } from '../services/api'

type AuthMode = 'login' | 'forgot-request' | 'forgot-reset'

/**
 * Khatam - the eight-point star of madrasah tilework, a diamond overlaid on a
 * square. Used twice: as the panel tessellation, and as the submit button's
 * loading mark.
 */
function KhatamMark({ size = 18, className }: { size?: number; className?: string }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M24 3 45 24 24 45 3 24Z" />
      <rect x="9" y="9" width="30" height="30" />
    </svg>
  )
}

function KhatamTessellation() {
  return (
    <svg aria-hidden="true" className="absolute inset-0 h-full w-full text-wicend-300/[0.07]">
      <defs>
        <pattern id="khatam-tile" width="96" height="96" patternUnits="userSpaceOnUse">
          <g fill="none" stroke="currentColor" strokeWidth="1">
            <path d="M48 6 90 48 48 90 6 48Z" />
            <rect x="18" y="18" width="60" height="60" />
            <circle cx="48" cy="48" r="10" />
            <path d="M48 6V0M48 90v6M6 48H0M90 48h6" />
          </g>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#khatam-tile)" />
    </svg>
  )
}

function PanelIndex({ label, items }: { label: string; items: string }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emas/80">{label}</p>
      <p className="mt-2 text-sm text-teal-50/80">{items}</p>
    </div>
  )
}

export function LoginPage() {
  const { user, login, getHomePath } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const isExpired = searchParams.get('expired') === '1'
  const searchFrom = searchParams.get('from')

  // UI Modes: 'login', 'forgot-request' (email input), 'forgot-reset' (OTP & new password)
  const [mode, setMode] = useState<AuthMode>(() =>
    location.pathname === '/forgot-password' || location.pathname === '/lupa-password'
      ? 'forgot-request'
      : 'login',
  )

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [loading, setLoading] = useState(false)

  // Reset password state
  const [forgotEmail, setForgotEmail] = useState('')
  const [maskedPhone, setMaskedPhone] = useState('')
  const [otp, setOtp] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [cooldown, setCooldown] = useState(0)

  // Countdown timer for OTP resend
  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => clearInterval(timer)
  }, [cooldown])

  if (user) return <Navigate to={getHomePath(user.role)} replace />

  const submitLogin = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    setSuccessMessage('')
    setLoading(true)
    try {
      const session = await login(email, password)
      const requested = (location.state as { from?: string } | null)?.from || searchFrom
      navigate(requested || getHomePath(session.role), { replace: true })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Login gagal.')
    } finally {
      setLoading(false)
    }
  }

  const handleRequestOtp = async (event?: FormEvent) => {
    if (event) event.preventDefault()
    setError('')
    setSuccessMessage('')
    const target = forgotEmail.trim().toLowerCase()
    if (!target) {
      setError('Silakan masukkan email terdaftar Anda.')
      return
    }

    setLoading(true)
    try {
      const result = await requestPasswordResetOtp(target)
      setMaskedPhone(result.maskedPhone)
      setMode('forgot-reset')
      setCooldown(60)
      setSuccessMessage(
        `Kode verifikasi 6 digit telah dikirim ke WhatsApp ${result.maskedPhone}.`,
      )
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : 'Gagal mengirim kode verifikasi WhatsApp.',
      )
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    setSuccessMessage('')

    const cleanOtp = otp.trim()
    if (!cleanOtp || cleanOtp.length !== 6) {
      setError('Kode verifikasi OTP harus 6 digit angka.')
      return
    }

    if (newPassword.length < 8) {
      setError('Kata sandi baru minimal 8 karakter.')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Konfirmasi kata sandi tidak cocok dengan kata sandi baru.')
      return
    }

    setLoading(true)
    try {
      const result = await resetPasswordWithOtp({
        email: forgotEmail.trim().toLowerCase(),
        otp: cleanOtp,
        newPassword,
      })
      // Reset form states & switch back to login mode
      setMode('login')
      setEmail(forgotEmail.trim().toLowerCase())
      setPassword('')
      setOtp('')
      setNewPassword('')
      setConfirmPassword('')
      setSuccessMessage(result.message || 'Kata sandi berhasil diperbarui! Silakan masuk.')
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'Gagal memperbarui kata sandi.',
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="grid min-h-screen bg-slate-50 lg:grid-cols-[0.85fr_1fr] dark:bg-slate-950">
      <section className="relative hidden overflow-hidden bg-wicend-950 p-12 text-white lg:flex lg:flex-col lg:justify-between xl:p-16">
        <KhatamTessellation />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-emas/0 via-emas to-emas/0" />

        <div className="relative">
          <Logo theme="dark" />
        </div>

        <div className="relative max-w-lg">
          <h1 className="font-display text-[2.75rem] font-extrabold leading-[1.1] tracking-[-0.02em] xl:text-5xl">
            Satu sistem untuk seluruh amanah sekolah.
          </h1>
          <p className="mt-7 max-w-md text-[15px] leading-7 text-teal-50/70">
            Sekolah Islam Terpadu Widya Cendekia membentuk generasi berakhlak mulia, cerdas, dan
            berdaya saing - dengan kurikulum terpadu yang memadukan keunggulan akademik dan
            nilai-nilai keislaman.
          </p>
        </div>

        <div className="relative">
          <div className="grid max-w-md grid-cols-2 gap-8 border-t border-white/10 pt-7">
            <PanelIndex label="Jenjang Pendidikan" items="TK Islam · SD Islam · SMP Islam" />
            <PanelIndex label="Keunggulan" items="Tahfidz · Bilingual · Karakter Islami" />
          </div>
          <p className="mt-8 text-xs text-teal-100/40">
            Sistem Informasi Manajemen Terpadu · Widya Cendekia
          </p>
        </div>
      </section>

      <section className="flex items-center justify-center px-5 py-10 md:px-10">
        <div className="w-full max-w-[26rem]">
          <div className="mb-10 lg:hidden">
            <Logo />
          </div>

          {/* Mode 1: LOGIN FORM */}
          {mode === 'login' && (
            <>
              <p className="font-display text-sm font-semibold tracking-tight text-wicend-600 dark:text-wicend-300">
                Assalamu&rsquo;alaikum
              </p>
              <h2 className="font-display mt-1.5 text-[1.75rem] font-extrabold tracking-[-0.02em] text-slate-950 dark:text-white">
                Masuk ke sistem
              </h2>
              <p className="mt-2.5 text-sm leading-6 text-slate-500 dark:text-slate-400">
                Gunakan email dan password yang diberikan administrator.
              </p>

              <form onSubmit={submitLogin} className="mt-9 space-y-5" data-testid="login-form">
                <label className="block">
                  <span className="mb-2 block text-[13px] font-semibold text-slate-700 dark:text-slate-200">
                    Email
                  </span>
                  <input
                    data-testid="login-email-input"
                    className="wicend-input h-12"
                    type="email"
                    name="email"
                    autoComplete="email"
                    spellCheck={false}
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="nama@wicend.id"
                  />
                </label>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label
                      htmlFor="password-input"
                      className="block text-[13px] font-semibold text-slate-700 dark:text-slate-200"
                    >
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setForgotEmail(email)
                        setError('')
                        setSuccessMessage('')
                        setMode('forgot-request')
                      }}
                      className="text-xs font-semibold text-wicend-600 transition-colors hover:text-wicend-700 hover:underline dark:text-wicend-400 dark:hover:text-wicend-300"
                      data-testid="login-forgot-password-link"
                    >
                      Lupa password?
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      id="password-input"
                      data-testid="login-password-input"
                      className="wicend-input h-12 pr-12"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="Masukkan password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-slate-400 transition-colors hover:text-slate-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-wicend-500 dark:hover:text-slate-200"
                      aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                      data-testid="login-password-visibility-btn"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {successMessage && (
                  <div
                    role="status"
                    aria-live="polite"
                    className="flex items-center gap-2 rounded-lg border-l-2 border-emerald-500 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300"
                  >
                    <CheckCircle2 size={16} className="shrink-0 text-emerald-600" />
                    <span>{successMessage}</span>
                  </div>
                )}

                {isExpired && !error && (
                  <div
                    role="status"
                    aria-live="polite"
                    data-testid="login-session-expired-alert"
                    className="rounded-lg border-l-2 border-amber-500 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800 dark:bg-amber-950/30 dark:text-amber-300"
                  >
                    Sesi Anda telah berakhir atau tidak valid. Silakan masuk kembali.
                  </div>
                )}

                {error && (
                  <div
                    role="alert"
                    aria-live="polite"
                    data-testid="login-error-alert"
                    className="rounded-lg border-l-2 border-red-500 bg-red-50 px-4 py-3 text-sm font-medium text-red-800 dark:bg-red-950/30 dark:text-red-300"
                  >
                    {error}
                  </div>
                )}

                <Button
                  fullWidth
                  size="lg"
                  variant="primary"
                  type="submit"
                  isDisabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <KhatamMark className="animate-spin" /> Memproses
                    </>
                  ) : (
                    <>
                      Masuk <ArrowRight size={18} />
                    </>
                  )}
                </Button>
              </form>

              <p className="mt-8 text-center text-sm text-slate-500 dark:text-slate-400">
                <Link
                  to="/"
                  className="font-semibold text-wicend-700 underline-offset-4 hover:underline dark:text-wicend-300"
                  data-testid="login-public-spmb-link"
                >
                  Lihat halaman SPMB
                </Link>
              </p>
            </>
          )}

          {/* Mode 2: FORGOT PASSWORD - REQUEST OTP */}
          {mode === 'forgot-request' && (
            <>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-wicend-50 px-3 py-1 text-xs font-semibold text-wicend-700 dark:bg-wicend-950/60 dark:text-wicend-300">
                <KeyRound size={13} />
                <span>Reset Kata Sandi</span>
              </div>
              <h2 className="font-display mt-3 text-[1.75rem] font-extrabold tracking-[-0.02em] text-slate-950 dark:text-white">
                Lupa kata sandi?
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                Masukkan email Anda. Kode verifikasi (OTP) 6 digit akan langsung dikirimkan ke nomor WhatsApp terdaftar.
              </p>

              <form onSubmit={handleRequestOtp} className="mt-8 space-y-5" data-testid="forgot-request-form">
                <label className="block">
                  <span className="mb-2 block text-[13px] font-semibold text-slate-700 dark:text-slate-200">
                    Email Terdaftar
                  </span>
                  <input
                    data-testid="forgot-email-input"
                    className="wicend-input h-12"
                    type="email"
                    name="forgotEmail"
                    autoComplete="email"
                    autoFocus
                    required
                    value={forgotEmail}
                    onChange={(event) => setForgotEmail(event.target.value)}
                    placeholder="nama@wicend.id"
                  />
                </label>

                {error && (
                  <div
                    role="alert"
                    aria-live="polite"
                    className="rounded-lg border-l-2 border-red-500 bg-red-50 px-4 py-3 text-sm font-medium text-red-800 dark:bg-red-950/30 dark:text-red-300"
                  >
                    {error}
                  </div>
                )}

                <Button
                  fullWidth
                  size="lg"
                  variant="primary"
                  type="submit"
                  isDisabled={loading}
                  data-testid="forgot-send-otp-btn"
                >
                  {loading ? (
                    <>
                      <KhatamMark className="animate-spin" /> Mengirim OTP...
                    </>
                  ) : (
                    <>
                      Kirim Kode via WhatsApp <MessageSquare size={17} />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-8 border-t border-slate-200 pt-5 text-center dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    setError('')
                    setSuccessMessage('')
                    setMode('login')
                  }}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 transition-colors hover:text-wicend-700 dark:text-slate-400 dark:hover:text-wicend-300"
                  data-testid="forgot-back-to-login-btn"
                >
                  <ArrowLeft size={16} /> Kembali ke halaman login
                </button>
              </div>
            </>
          )}

          {/* Mode 3: FORGOT PASSWORD - VERIFY OTP & RESET PASSWORD */}
          {mode === 'forgot-reset' && (
            <>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
                <MessageSquare size={13} />
                <span>Verifikasi WhatsApp</span>
              </div>
              <h2 className="font-display mt-3 text-[1.75rem] font-extrabold tracking-[-0.02em] text-slate-950 dark:text-white">
                Atur kata sandi baru
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                Kode 6 digit telah dikirim ke nomor WhatsApp{' '}
                <span className="font-semibold text-slate-800 dark:text-slate-200">{maskedPhone || 'Anda'}</span>.
              </p>

              <form onSubmit={handleResetPassword} className="mt-7 space-y-4" data-testid="forgot-reset-form">
                {successMessage && (
                  <div
                    role="status"
                    aria-live="polite"
                    className="flex items-center gap-2 rounded-lg border-l-2 border-emerald-500 bg-emerald-50 px-3.5 py-2.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300"
                  >
                    <CheckCircle2 size={15} className="shrink-0 text-emerald-600" />
                    <span>{successMessage}</span>
                  </div>
                )}

                <label className="block">
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="block text-[13px] font-semibold text-slate-700 dark:text-slate-200">
                      Kode OTP (6 Digit)
                    </span>
                    {cooldown > 0 ? (
                      <span className="text-xs text-slate-400">
                        Kirim ulang ({cooldown}d)
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleRequestOtp()}
                        disabled={loading}
                        className="text-xs font-semibold text-wicend-600 hover:underline dark:text-wicend-400"
                      >
                        Kirim ulang kode
                      </button>
                    )}
                  </div>
                  <input
                    data-testid="forgot-otp-input"
                    className="wicend-input h-12 text-center font-mono text-xl font-bold tracking-[0.3em]"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    autoFocus
                    required
                    value={otp}
                    onChange={(event) => setOtp(event.target.value.replace(/\D/g, ''))}
                    placeholder="123456"
                  />
                </label>

                <div>
                  <label
                    htmlFor="new-password-input"
                    className="mb-1.5 block text-[13px] font-semibold text-slate-700 dark:text-slate-200"
                  >
                    Kata Sandi Baru
                  </label>
                  <div className="relative">
                    <input
                      id="new-password-input"
                      data-testid="forgot-new-password-input"
                      className="wicend-input h-12 pr-12"
                      type={showNewPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={newPassword}
                      onChange={(event) => setNewPassword(event.target.value)}
                      placeholder="Minimal 8 karakter"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((value) => !value)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-slate-400 transition-colors hover:text-slate-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-wicend-500 dark:hover:text-slate-200"
                      aria-label={showNewPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="confirm-password-input"
                    className="mb-1.5 block text-[13px] font-semibold text-slate-700 dark:text-slate-200"
                  >
                    Konfirmasi Kata Sandi Baru
                  </label>
                  <input
                    id="confirm-password-input"
                    data-testid="forgot-confirm-password-input"
                    className="wicend-input h-12"
                    type={showNewPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder="Ulangi kata sandi baru"
                  />
                </div>

                {error && (
                  <div
                    role="alert"
                    aria-live="polite"
                    className="rounded-lg border-l-2 border-red-500 bg-red-50 px-4 py-3 text-sm font-medium text-red-800 dark:bg-red-950/30 dark:text-red-300"
                  >
                    {error}
                  </div>
                )}

                <Button
                  fullWidth
                  size="lg"
                  variant="primary"
                  type="submit"
                  isDisabled={loading}
                  data-testid="forgot-reset-submit-btn"
                  className="mt-2"
                >
                  {loading ? (
                    <>
                      <KhatamMark className="animate-spin" /> Menyimpan...
                    </>
                  ) : (
                    <>
                      Simpan Kata Sandi & Masuk <ArrowRight size={18} />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-7 border-t border-slate-200 pt-4 text-center dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    setError('')
                    setSuccessMessage('')
                    setMode('login')
                  }}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 transition-colors hover:text-wicend-700 dark:text-slate-400 dark:hover:text-wicend-300"
                >
                  <ArrowLeft size={16} /> Batal & kembali ke login
                </button>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  )
}
